import { useLanguage } from "../App";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { Play, Layers, Check, Sparkles } from "lucide-react";
import { trackAnalysisCtaClick } from "../utils/analytics";

export const Hero = ({ onStartAnalysis }) => {
  const { t } = useLanguage();

  const handleViewSolutions = () => {
    const el = document.getElementById("ged");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="hero-gradient min-h-[88vh] pt-20 sm:pt-24 pb-16 px-4 md:px-8 lg:px-12 relative overflow-hidden flex items-center"
      data-testid="hero-section"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500 rounded-full blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto relative z-10 w-full text-center">
        {/* Eyebrow badge */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs font-semibold uppercase tracking-wider mb-6"
          data-testid="hero-eyebrow"
        >
          <Sparkles size={14} className="text-blue-300" />
          Documatch · AI-powered DMS selection
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.5 }}
          className="font-playfair text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight text-white mb-5 max-w-4xl mx-auto"
          data-testid="hero-title"
        >
          {t.hero.title}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="text-base sm:text-lg lg:text-xl text-white/80 leading-relaxed mb-8 max-w-3xl mx-auto"
          data-testid="hero-description"
        >
          {t.hero.description}
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-10"
        >
          <Button
            onClick={() => {
              trackAnalysisCtaClick("hero_primary");
              onStartAnalysis();
            }}
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-6 rounded-xl text-base sm:text-lg animate-pulse-glow shadow-lg shadow-blue-900/40"
            data-testid="cta-start-analysis"
          >
            <Play size={20} className="mr-2" />
            {t.hero.cta}
          </Button>

          <Button
            onClick={handleViewSolutions}
            variant="outline"
            size="lg"
            className="bg-white/10 hover:bg-white/20 text-white border-white/30 hover:border-white/50 font-semibold px-8 py-6 rounded-xl text-base sm:text-lg backdrop-blur-md"
            data-testid="cta-view-solutions"
          >
            <Layers size={20} className="mr-2" />
            {t.hero.ctaSecondary || t.hero.faq}
          </Button>
        </motion.div>

        {/* Trust indicators */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-7 text-white/85 text-xs sm:text-sm"
          data-testid="hero-trust-indicators"
        >
          {[t.hero.trust1, t.hero.trust2, t.hero.trust3].map((label, i) => (
            label && (
              <span key={i} className="flex items-center gap-2" data-testid={`hero-trust-${i + 1}`}>
                <span className="w-5 h-5 rounded-full bg-green-500/20 border border-green-400/40 flex items-center justify-center flex-shrink-0">
                  <Check size={12} className="text-green-300" strokeWidth={3} />
                </span>
                {label}
              </span>
            )
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
